import { Controller } from '@hotwired/stimulus'

const CHIP = '.choices__list--multiple .choices__item[data-value]'

// Lets the admin drag the selected pieces of the "Data Piece" multi-select to
// change their order. The native <select> can't carry an order of its own, so
// the arrangement is mirrored into hidden `piece_order` inputs that the server
// applies once the event is saved.
export default class PieceOrderController extends Controller {
  static targets = ['select', 'inputs']
  static values = { inputName: String, initialOrder: Array }

  connect () {
    this.order = this.initialOrderValue.map(String)
    this.dragged = null
    this.boundMouseDown = this.#mouseDown.bind(this)
    this.boundDragStart = this.#dragStart.bind(this)
    this.boundDragOver = this.#dragOver.bind(this)
    this.boundDragEnd = this.#dragEnd.bind(this)

    // Choices builds its markup after this controller connects, and rebuilds the
    // chips on every add/remove, so watch the wrapper and re-apply the order.
    this.wrapper = this.selectTarget.closest('[data-controller~="choices"]')
    this.observer = new window.MutationObserver(() => this.#apply())
    this.#observe()

    this.wrapper.addEventListener('mousedown', this.boundMouseDown, true)
    this.wrapper.addEventListener('dragstart', this.boundDragStart)
    this.wrapper.addEventListener('dragover', this.boundDragOver)
    this.wrapper.addEventListener('dragend', this.boundDragEnd)
    this.#apply()
  }

  disconnect () {
    this.observer?.disconnect()
    this.wrapper?.removeEventListener('mousedown', this.boundMouseDown, true)
    this.wrapper?.removeEventListener('dragstart', this.boundDragStart)
    this.wrapper?.removeEventListener('dragover', this.boundDragOver)
    this.wrapper?.removeEventListener('dragend', this.boundDragEnd)
  }

  #observe () {
    this.observer.observe(this.wrapper, { childList: true, subtree: true })
  }

  #chips () {
    return Array.from(this.wrapper.querySelectorAll(CHIP))
  }

  // Puts the chips in the remembered order, makes them draggable and refreshes
  // the hidden inputs. Newly picked pieces go to the end.
  #apply () {
    // Until Choices has built its markup there are no chips to compare against, and
    // filtering the saved order by an empty list would throw it away.
    if (this.dragged || !this.wrapper.querySelector('.choices')) return

    const chips = this.#chips()
    const byId = new Map(chips.map(chip => [chip.dataset.value, chip]))

    this.order = this.order.filter(id => byId.has(id))
    byId.forEach((_chip, id) => {
      if (!this.order.includes(id)) this.order.push(id)
    })

    // Moving nodes is itself a DOM mutation; don't react to our own changes.
    this.observer.disconnect()
    this.order.forEach(id => byId.get(id).parentElement.append(byId.get(id)))
    chips.forEach(chip => { chip.draggable = true })
    this.#syncInputs()
    this.#observe()
  }

  #syncInputs () {
    const inputs = this.order.map(id => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = this.inputNameValue
      input.value = id
      return input
    })

    this.inputsTarget.replaceChildren(...inputs)
  }

  // Choices cancels every mousedown inside it (capture phase, preventDefault), which
  // also cancels the browser's native drag. This listener sits on an ancestor, so in
  // the capture phase it runs first and keeps chip presses away from Choices — except
  // the remove button, which Choices still has to handle.
  #mouseDown (event) {
    if (event.target.closest?.('[data-button]')) return

    if (event.target.closest?.(CHIP)) event.stopPropagation()
  }

  #dragStart (event) {
    const chip = event.target.closest?.(CHIP)
    if (!chip) return

    this.dragged = chip
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', chip.dataset.value)
    chip.classList.add('opacity-50')
  }

  // Moves the dragged chip live, so the admin sees where it will land.
  #dragOver (event) {
    if (!this.dragged) return

    event.preventDefault()
    const target = event.target.closest?.(CHIP)
    if (!target || target === this.dragged) return

    const box = target.getBoundingClientRect()
    const before = event.clientX < box.left + box.width / 2
    target.parentElement.insertBefore(this.dragged, before ? target : target.nextSibling)
  }

  #dragEnd () {
    if (!this.dragged) return

    this.dragged.classList.remove('opacity-50')
    this.dragged = null
    this.order = this.#chips().map(chip => chip.dataset.value)
    this.#apply()
  }
}
